import { z } from 'zod';
/**
 * Canonical API-level error codes — distinct from `SSE_ERROR_CODES`
 * (stream-terminating SSE errors) and `TOOL_ERROR_CODES` (recoverable
 * tool-level failures). API errors ride HTTP response body's `code` field
 * (e.g. HTTP 426 `{code: 'PROTOCOL_VERSION_REQUIRED', requiredVersion: 'v2'}`).
 *
 * Web consumers read via the response body's `code` field. The api-side
 * canonical constants live at `apps/api/src/core/errors/error-codes.ts`;
 * this shared-types export is the wire contract so web can match against
 * literal strings type-safely.
 *
 * Per ADR-0007 + research/phase-1.md § 11 risk #4, this constant is a
 * sibling of SSE_ERROR_CODES (8 codes) + TOOL_ERROR_CODES (9 codes); a NEW
 * surface, not an extension of either.
 */
export const API_ERROR_CODES = {
    /**
     * Server raised `inputs_provenance_drift` in `record_edit_txn` RPC
     * (spec §5). Maps to HTTP 500. Indicates a desync between
     * `p_input_values_delta` and `p_input_provenance_delta` at write-time.
     * Should be impossible post-Phase-2 once all input-writing tools route
     * through `recordEdit` which writes provenance-only; the invariant is
     * defensive.
     */
    PROVENANCE_DRIFT: 'PROVENANCE_DRIFT',
    /**
     * Client connected with `X-Arcanum-Protocol: v1` (or missing header)
     * after the feature ships. Maps to HTTP 426 on REST endpoints. For SSE
     * endpoints, a separate `sseProtocolChangedEventSchema` variant fires
     * instead — this code only rides REST 426 bodies.
     */
    PROTOCOL_VERSION_REQUIRED: 'PROTOCOL_VERSION_REQUIRED',
};
/**
 * Zod schema for parsing the `code` field on an HTTP error response body.
 * Permissive — accepts unknown codes (other features may add codes outside
 * this constant). Use the constant for type-safe matching:
 *   `if (body.code === API_ERROR_CODES.PROTOCOL_VERSION_REQUIRED) { ... }`
 */
export const apiErrorCodeSchema = z.string();
